<template>
    <div>
        <page-header
            :title="isEditMode ? $t('pages.admin.video.upload.page_title_edit') : $t('pages.admin.video.upload.page_title')"
                     icon-class="mdi mdi-frequently-asked-questions">
            <a class="btn btn-sm btn-dark float-right mb-4" :href="$router.resolve({name: 'gym.video', params: {video: this.$route.params.video}}).href" target="_blank" v-if="isEditMode">
                Video ukucke
            </a>
        </page-header>

        <div class="row">
            <div class="col-12 grid-margin stretch-card">
                <div class="card shadow-sm">
                    <div class="card-body">
                        <static-loading-indicator-icon v-if="!showUploadProgress"/>

                        <div class="row" v-if="showUploadProgress">
                            <div class="col-md-12">
                                <h5 class="card-title">Upload progress ({{ videoUploadProgress }}%)</h5>
                                <div class="progress">
                                    <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar"
                                         :aria-valuenow="videoUploadProgress" aria-valuemin="0" aria-valuemax="100" :style="'width:' + videoUploadProgress + '%'"></div>
                                </div>

                                <div class="alert alert-info mt-3">
                                    {{ $t('pages.admin.video.upload.uploading.dont_leave_page') }}
                                </div>
                            </div>
                        </div>

                        <div v-if="!$store.getters.shouldLoadLocal">
                            <div class="form-group" v-if="isEditMode">
                                <strong>Thumbnail</strong>
                                <div class="row">
                                    <div class="col-md-4 mt-3">
                                        <img :src="existingThumbnailUrl" class="img-fluid" style="border-radius: 8px; max-height: 200px" v-if="existingThumbnailUrl !== null">
                                    </div>
                                    <div class="col-md-8">
                                        <input type="file" class="file-upload-default" id="thumbnail-default" :class="{'is-invalid': $errors.has('thumbnail')}" @change="thumbnailSelected">
                                        <div class="input-group cursor-pointer col-xs-12" @click="showSelectThumbnailDialog">
                                            <input type="text" class="form-control file-upload-info" id="thumbnail-upload" disabled="" placeholder="Auswielen">
                                            <span class="input-group-append">
                                                <button class="file-upload-browse btn btn-gradient-primary" type="button">Thumbnail ersetze</button>
                                            </span>
                                            <error input="thumbnail"/>
                                        </div>
                                        <div class="text-muted mt-2">
                                            D'Bild muss eng Opléisung vun 1920x1080 hun.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <language-switcher-nav :language.sync="selectedLanguage">
                                <div class="form-group">
                                    <label for="title">
                                        <strong>
                                            {{ $t('pages.admin.video.upload.title') }}
                                        </strong>
                                    </label>

                                    <input type="text" id="title" class="form-control" v-model="title[selectedLanguage]" :placeholder="$t('pages.admin.video.upload.title')" :class="{'is-invalid': $errors.has('title')}">
                                    <error :input="'title.' + selectedLanguage"/>
                                </div>

                                <div class="form-group">
                                    <label for="equipment">
                                        <strong>
                                            {{ $t('pages.admin.video.upload.equipment_needed') }}
                                        </strong>
                                    </label>
                                    <textarea id="equipment" class="form-control" v-model="equipment[selectedLanguage]" :placeholder="$t('pages.admin.video.upload.equipment_needed')"></textarea>
                                    <error :input="'equipment.' + selectedLanguage"/>
                                </div>
                            </language-switcher-nav>

                            <div class="form-group" v-if="!isEditMode">
                                <label>
                                    <strong>
                                        {{ $t('pages.admin.video.upload.select_video') }}
                                    </strong>
                                </label>
                                <input type="file" class="file-upload-default" id="file" :class="{'is-invalid': $errors.has('file')}" @change="fileSelected">
                                <div class="input-group cursor-pointer col-xs-12" @click="showSelectFileDialog">
                                    <input type="text" class="form-control file-upload-info" disabled="" :placeholder="$t('pages.admin.video.upload.select_video')">
                                    <span class="input-group-append">
                                    <button class="file-upload-browse btn btn-gradient-primary" type="button">{{ $t('actions.select') }}</button>
                                </span>
                                    <error input="file"/>
                                </div>
                            </div>

                            <div class="form-group">
                                <strong>{{ $t('pages.admin.video.upload.select_goals') }}</strong>
                                <div class="row">
                                    <div class="col-4 pt-3" v-for="(group, i) in goalsByGroup">
                                        <div class="card bg-light rounded">
                                            <div class="card-body">
                                                <strong>
                                                    <span>{{ $t('pages.admin.video.upload.goal_group_' + i) }}</span>
                                                </strong>
                                                <div class="form-check form-check-primary" v-for="item in group">
                                                    <label class="form-check-label">
                                                        <input type="checkbox" class="form-check-input" :checked="selectedGoals.includes(item.id)" @click="addOrRemoveGoal(item.id)">
                                                        {{ item.label }}
                                                        <i class="input-helper"></i>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <error input="selected_goals"/>
                            </div>

                            <div class="form-group">
                                <label for="preferred_language">
                                    <strong>{{ $t("pages.admin.video.upload.select_series") }}</strong>
                                </label>
                                <select class="form-control" id="preferred_language" :placeholder="$t('pages.admin.video.upload.select_series')"  v-model="series_id"
                                        :class="{'is-invalid': $errors.has('series_id')}">
                                    <option value="">--- {{ $t('pages.admin.video.upload.no_series') }} ---</option>
                                    <option :value="series.id" v-for="series in seriesList">{{ series.title[$i18n.locale] }}</option>
                                </select>
                                <error input="series_id"/>
                                <small class="form-text text-muted">{{ $t('pages.admin.video.upload.series_explanation') }}</small>
                            </div>

                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="intensity">
                                            <strong>{{ $t('pages.admin.video.upload.intensity') }}:</strong> {{ intensity }}
                                        </label>
                                        <range-slider id="intensity" class="slider" min="1" max="10" step="1" v-model="intensity"/>
                                        <error input="intensity"/>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label for="level">
                                            <strong>{{ $t('pages.admin.video.upload.level') }}:</strong> {{ labelByLevel }}
                                        </label>
                                        <range-slider id="level" class="slider" min="1" max="3" step="1" v-model="level"/>
                                        <error input="level"/>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-md-12">
                                    <div class="form-group">
                                        <label for="duration">
                                            <strong>{{ $t('pages.admin.video.upload.duration_in_minutes') }}</strong>
                                        </label>
                                        <input type="number" class="form-control" min="1" max="240" id="duration"
                                               :placeholder="$t('pages.admin.video.upload.duration_in_minutes')"
                                               v-model="duration_in_minutes" @change="durationChanged">
                                        <error input="duration"/>
                                    </div>
                                </div>
                            </div>

                            <button class="btn btn-primary" @click="save">
                                {{ isEditMode ? $t('actions.update') : $t('actions.upload') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from "../../Includes/PageHeader";
import StaticLoadingIndicatorIcon from "../../Includes/StaticLoadingIndicatorIcon";
import AdminVideoRepository from "../../../repositories/Admin/AdminVideoRepository";

import RangeSlider from 'vue-range-slider'
import LanguageSwitcherNav from "../../Includes/LanguageSwitcherNav";
import AdminSeriesRepository from "../../../repositories/Admin/AdminSeriesRepository";
export default {
    name: "UploadEditVideo",
    components: {LanguageSwitcherNav, StaticLoadingIndicatorIcon, PageHeader, RangeSlider},
    data: () => ({
        title: {
            de: '',
            en: '',
            lb: ''
        },
        file: null,
        newThumbnail: null,
        intensity: 1,
        level: 1,
        duration_in_minutes: 20,
        equipment: {
            de: '',
            en: '',
            lb: '',
        },
        selectedGoals: [],
        series_id: '',

        showUploadProgress: false,

        selectedLanguage: 'en',
        goals: [],
        seriesList: [],

        existingThumbnailUrl: null,
    }),
    methods: {
        /* File Upload */
        thumbnailSelected(e) {
            this.newThumbnail = e.target.files[0];
            let fileUploadInfoInput = document.getElementById('thumbnail-upload');
            console.log(fileUploadInfoInput);
            fileUploadInfoInput.value = this.newThumbnail.name;
            fileUploadInfoInput.value = fileUploadInfoInput.value.replace(/C:\\fakepath\\/i, '');
        },
        showSelectThumbnailDialog() {
            let file = document.getElementById('thumbnail-default');
            file.click();
        },


        fileSelected(e) {
            this.file = e.target.files[0];
            let fileUploadInfoInput = document.getElementsByClassName('file-upload-info')[0];
            fileUploadInfoInput.value = this.file.name;
            fileUploadInfoInput.value = fileUploadInfoInput.value.replace(/C:\\fakepath\\/i, '');
        },
        uploadVideo() {
            let formData = new FormData();

            if(this.file !== null)
                formData.append('file', this.file);

            formData.append('title[de]', this.title.de);
            formData.append('title[en]', this.title.en);
            formData.append('title[lb]', this.title.lb);
            formData.append('intensity', this.intensity);
            formData.append('level', this.level);
            formData.append('duration', this.getDurationInSeconds);

            console.log(this.series_id);
            console.log(typeof this.series_id);
            if(this.series_id !== 'null' && this.series_id !== null)
                formData.append('series_id', this.series_id);


            formData.append('equipment[de]', this.equipment.de);
            formData.append('equipment[en]', this.equipment.en);
            formData.append('equipment[lb]', this.equipment.lb);

            for(let i  = 0; i < this.selectedGoals.length; i++) {
                formData.append('selected_goals[]', this.selectedGoals[i]);
            }

            this.$store.commit('setVideoUploadProgress', 0);
            this.showUploadProgress = true;
            return AdminVideoRepository
                .uploadVideo(formData)
                .then((res) => {
                    this.$snotify.success(this.$t('pages.admin.video.upload.video_uploaded'))
                    this.$router.push({name: 'admin.video.index'});
                }).finally(() => this.showUploadProgress = false);
        },
        showSelectFileDialog() {
              let file = document.getElementsByClassName('file-upload-default')[0];
              file.click();
        },
        durationChanged() {
            let duration_in_minutes = parseInt(this.duration_in_minutes);
            if(duration_in_minutes < 1)
                this.duration_in_minutes = 1;
            else if(duration_in_minutes > 240)
                this.duration_in_minutes = 240;
        },
        addOrRemoveGoal(goalId) {
            if(this.selectedGoals.includes(goalId)) {
                this.selectedGoals = this.selectedGoals.filter((g => g !== goalId));
            } else {
                this.selectedGoals.push(goalId);
            }
        },

        save() {
            if(this.isEditMode)
                return this.updateVideo();
            else return this.uploadVideo();
        },

        getVideo() {
            if(!this.isEditMode)
                return false;

            AdminVideoRepository
                .getVideo(this.videoToEdit)
                .then((res) => {
                    let data = res.data;

                    this.title = {de: data.title.de, en: data.title.en, lb: data.title.lb};
                    this.intensity = data.intensity;
                    this.series_id = data.series !== null ? data.series.id : "";
                    this.level = data.level;
                    this.duration_in_minutes = (data.duration / 60);
                    this.equipment = {de: data.equipment.de, en: data.equipment.en, lb: data.equipment.lb};
                    this.selectedGoals = data.goals.flatMap((g) => g.id);

                    if(data.thumbnail_url !== undefined) {
                        this.existingThumbnailUrl = data.thumbnail_url;
                    }
                });
        },
        updateVideo() {
            let formData = new FormData();

            formData.append('title[de]', this.title.de);
            formData.append('title[en]', this.title.en);
            formData.append('title[lb]', this.title.lb);
            formData.append('intensity', this.intensity);
            formData.append('series_id', this.series_id);
            formData.append('level', this.level);
            formData.append('duration', this.getDurationInSeconds);
            formData.append('equipment[de]', this.equipment.de);
            formData.append('equipment[en]', this.equipment.en);
            formData.append('equipment[lb]', this.equipment.lb);

            if(this.newThumbnail !== null)
                formData.append('thumbnail', this.newThumbnail);

            for(let i  = 0; i < this.selectedGoals.length; i++) {
                formData.append('selected_goals[]', this.selectedGoals[i]);
            }

            return AdminVideoRepository
                .updateVideo(this.videoToEdit, formData)
                .then((res) => {
                    this.$snotify.success(this.$t('actions.updated'))
                    this.$router.push({name: 'admin.video.index'});
                });
        }
    },
    computed: {
        videoToEdit() {
            return this.$route.params.video;
        },
        isEditMode() {
            return this.videoToEdit !== undefined;
        },

        getDurationInSeconds() {
            return (parseInt(this.duration_in_minutes) * 60);
        },
        goalsByGroup() {
            return _.groupBy(this.goals, goal => goal.group);
        },
        labelByLevel() {
            switch (this.level) {
                case 1:
                    return this.$t('pages.admin.video.upload.level_fundamental');
                case 2:
                    return this.$t('pages.admin.video.upload.level_challenge');
                case 3:
                    return this.$t('pages.admin.video.upload.level_advanced');
                default:
                    return "???";
            }
        },
        videoUploadProgress() {
            return this.$store.state.admin.video_upload_progress;
        }
    },
    mounted() {
        AdminVideoRepository
            .getGoals()
            .then((res) => this.goals = res.data);

        AdminSeriesRepository
            .getAllSeries()
            .then((res) => this.seriesList = res.data);

        if(this.isEditMode) {
            this.getVideo();
        }
    }
}
</script>

<style scoped>

</style>
